import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../contexts/AuthContext';
import { PROFILE_MENU_CLOSE_DELAY_MS } from '../../constants/timing';
import { logger } from '../../utils/logger';
import LanguageSelection from './components/LanguageSelection';
import ThemeMode from './components/ThemeMode';

interface ProfileDetailsProps {
  showName?: boolean;
}

type MenuKey = 'profile' | 'language' | 'theme' | 'logout';

interface MenuItem {
  key: MenuKey;
  labelKey: string;
  dividerAbove?: boolean;
  /** When set, the row renders this widget alongside the label and the row is non-clickable. */
  renderTrailing?: () => React.ReactNode;
}

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
const FALLBACK_USER_NAME = 'User';

const MENU: ReadonlyArray<MenuItem> = [
  { key: 'profile', labelKey: 'header.profile.profile' },
  {
    key: 'language',
    labelKey: 'header.profile.language',
    renderTrailing: () => <LanguageSelection compact />,
  },
  {
    key: 'theme',
    labelKey: 'header.profile.theme',
    dividerAbove: true,
    renderTrailing: () => <ThemeMode />,
  },
  { key: 'logout', labelKey: 'header.profile.logout', dividerAbove: true },
];

const DEFAULT_AVATAR_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23bbb">' +
  '<path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.4c-3.3 0-9.8 1.6-9.8 4.9v2.5h19.6v-2.5c0-3.3-6.5-4.9-9.8-4.9z"/>' +
  '</svg>';
const DEFAULT_AVATAR =
  'data:image/svg+xml;utf8,' + encodeURIComponent(DEFAULT_AVATAR_SVG);

// ── Tailwind class blocks ──────────────────────────────────────────────────
const CONTAINER_CLASS = 'relative inline-block font-[inherit]';

const PROFILE_BTN_CLASS =
  'inline-flex items-center gap-2 bg-transparent border-0 cursor-pointer' +
  ' py-1 px-2 rounded-lg text-[inherit] text-sm transition-colors duration-[150ms] ease-linear' +
  ' hover:bg-[var(--color-bg-hover)]';

// `.profile-name` was hidden on mobile (max-width:768px).
const PROFILE_NAME_CLASS =
  'max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap max-[768px]:hidden';

const PROFILE_AVATAR_CLASS =
  'w-7 h-7 rounded-full object-cover bg-[var(--color-bg-hover)]';

const PROFILE_CARET_CLASS = 'text-xs opacity-70';

// The menu is opened and closed by hovering the container, so the 6px
// visual gap between the trigger and the panel must belong to the hover
// region: it is PADDING on this positioned wrapper, not an offset on the
// panel. Offsetting the panel (`top: calc(100% + 6px)`) left the strip
// empty, and a pointer crossing it — or cutting diagonally across it on
// the compact header, where the container is only the avatar while the
// panel extends 220px to its left — left the container for longer than
// the close delay and dismissed the menu on the way to its first item.
const PROFILE_MENU_WRAP_CLASS = 'absolute right-0 top-full pt-[6px] z-[1000]';

// `.profile-menu`. Mobile keeps the same 220px min-width as desktop — a
// narrower menu (the old 160px) couldn't fit the "Language" row's label +
// the "Eng (US)" badge, so the badge overflowed/overlapped the menu edge.
const PROFILE_MENU_CLASS =
  'min-w-[220px] list-none m-0 py-[6px] px-0' +
  ' bg-[var(--color-bg-elevated)] border border-solid border-[var(--color-border)] rounded-lg' +
  ' shadow-[var(--shadow-elevated)]';

const PROFILE_MENU_ITEM_CLASS = 'block';

const PROFILE_MENU_BUTTON_CLASS =
  'w-full text-left bg-transparent border-0 py-[10px] px-4 text-sm text-[var(--color-text-primary)] cursor-pointer' +
  ' hover:bg-[var(--color-bg-hover)] focus:bg-[var(--color-bg-hover)] focus:outline-none';

const PROFILE_MENU_ROW_CLASS =
  'flex items-center justify-between gap-3 py-2 px-4 text-sm text-[var(--color-text-primary)]';

const PROFILE_MENU_LABEL_CLASS = 'flex-1';

const PROFILE_MENU_TRAILING_CLASS = 'flex-none inline-flex items-center';

const PROFILE_MENU_DIVIDER_CLASS =
  'h-px bg-[var(--color-border)] my-1 mx-0';

const matchesMobile = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches;

const pickDisplayName = (user: { name?: unknown; displayName?: unknown; email?: unknown } | null): string => {
  const candidates = [user?.name, user?.displayName, user?.email];
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return FALLBACK_USER_NAME;
};

const ProfileDetails: React.FC<ProfileDetailsProps> = ({ showName = true }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(matchesMobile);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent): void => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const clearCloseTimer = useCallback((): void => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback((): void => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(
      () => setOpen(false),
      PROFILE_MENU_CLOSE_DELAY_MS
    );
  }, [clearCloseTimer]);

  const handleTriggerClick = (): void => setOpen((prev) => !prev);

  const handleMouseEnter = (): void => {
    if (isMobile) return;
    clearCloseTimer();
    setOpen(true);
  };

  const handleMouseLeave = (): void => {
    if (isMobile) return;
    scheduleClose();
  };

  const handleSelect = async (key: MenuKey): Promise<void> => {
    setOpen(false);
    if (key === 'logout') {
      await logout();
      return;
    }
    logger.info('ProfileDetails: menu action not yet wired', key);
  };

  const name = pickDisplayName(user as { name?: unknown; displayName?: unknown; email?: unknown } | null);
  const pictureRaw = (user as { picture?: unknown } | null)?.picture;
  const picture =
    typeof pictureRaw === 'string' && pictureRaw.trim() !== ''
      ? pictureRaw
      : DEFAULT_AVATAR;

  return (
    <div // NOSONAR — hover handlers open/close the dropdown menu; container is not an interactive control
      className={CONTAINER_CLASS}
      ref={containerRef}
      data-testid="profile-menu-hover-region"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className={PROFILE_BTN_CLASS}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('header.profile.menu')}
        onClick={handleTriggerClick}
      >
        {showName ? <span className={PROFILE_NAME_CLASS}>{name}</span> : null}
        <img
          src={picture}
          alt="User profile"
          className={PROFILE_AVATAR_CLASS}
          referrerPolicy="no-referrer"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
          }}
        />
        <span className={PROFILE_CARET_CLASS} aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        <div className={PROFILE_MENU_WRAP_CLASS} data-testid="profile-menu-wrap">
        <ul className={PROFILE_MENU_CLASS} role="menu"> {/* NOSONAR — role="menu" on <ul> is correct ARIA for a dropdown action menu triggered by a button */}
          {MENU.map((item, idx) => (
            <React.Fragment key={item.key}>
              {item.dividerAbove && idx > 0 ? (
                <hr className={PROFILE_MENU_DIVIDER_CLASS} aria-hidden="true" />
              ) : null}
              <li className={PROFILE_MENU_ITEM_CLASS} role="none">
                {item.renderTrailing !== undefined ? (
                  <div className={PROFILE_MENU_ROW_CLASS}>
                    <span className={PROFILE_MENU_LABEL_CLASS}>{t(item.labelKey)}</span>
                    <span className={PROFILE_MENU_TRAILING_CLASS}>{item.renderTrailing()}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    role="menuitem" // NOSONAR
                    className={PROFILE_MENU_BUTTON_CLASS}
                    onClick={() => handleSelect(item.key)}
                  >
                    {t(item.labelKey)}
                  </button>
                )}
              </li>
            </React.Fragment>
          ))}
        </ul>
        </div>
      ) : null}
    </div>
  );
};

export default ProfileDetails;
